import React, { useState } from "react";
import { useLocation } from "react-router-dom";
import { useAuth } from "../utils/AuthContext";
import "./Header.css";
import { CgProfile } from "react-icons/cg";
import { FiEye, FiEyeOff } from "react-icons/fi";
import apiService from "../services/apiService";

const Header = ({ onMenuClick, currentPath }) => {
  const location = useLocation();
  const { logout, user } = useAuth();
  const [profileModalOpen, setProfileModalOpen] = useState(false);
  const [profileData, setProfileData] = useState(null);
  const [loadingProfile, setLoadingProfile] = useState(false);
  const [savingProfile, setSavingProfile] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const handleLogout = async () => {
    if (window.confirm('Are you sure you want to logout?')) {
      logout(); // Use the logout function from AuthContext
    }
  };

  const handleProfileClick = () => {
    setProfileModalOpen(true);
    if (user?.id) {
      fetchUserProfile();
    }
  };

  const fetchUserProfile = async () => {
    if (!user?.id) return;
    
    try {
      setLoadingProfile(true);
      const data = await apiService.get(`/users/${user.id}`);
      setProfileData(data);
      // Reset password fields when opening modal
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err) {
      console.error('Error fetching user profile:', err);
      alert('Failed to load profile. Please try again.');
    } finally {
      setLoadingProfile(false);
    }
  };

  const validatePassword = (password) => {
    const hasUpperCase = /[A-Z]/.test(password);
    const hasLowerCase = /[a-z]/.test(password);
    const hasNumber = /[0-9]/.test(password);
    const hasSpecialChar = /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password);
    const minLength = password.length >= 8;

    return {
      isValid: hasUpperCase && hasLowerCase && hasNumber && hasSpecialChar && minLength,
      hasUpperCase,
      hasLowerCase,
      hasNumber,
      hasSpecialChar,
      minLength
    };
  };

  const handleChangePassword = async () => {
    if (!currentPassword.trim()) {
      alert('Please enter your current password');
      return;
    }

    if (!newPassword.trim()) {
      alert('Please enter a new password');
      return;
    }

    const passwordValidation = validatePassword(newPassword);
    if (!passwordValidation.isValid) {
      alert('Password must be at least 8 characters and contain uppercase, lowercase, number, and special character');
      return;
    }

    if (newPassword !== confirmPassword) {
      alert('New password and confirm password do not match');
      return;
    }

    if (currentPassword === newPassword) {
      alert('New password must be different from current password');
      return;
    }

    try {
      setSavingProfile(true);
      
      const requestBody = {
        userId: user.id,
        currentPassword: currentPassword,
        newPassword: newPassword
      };

      await apiService.post('/auth/change-password', requestBody);
      
      // Reset password fields
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setShowNewPassword(false);
      setShowConfirmPassword(false);
      
      alert('Password changed successfully!');
      
    } catch (err) {
      console.error('Error changing password:', err);
      const errorMessage = err.response?.data?.message || err.message || 'Failed to change password. Please try again.';
      alert(errorMessage);
    } finally {
      setSavingProfile(false);
    }
  };

  const getPageTitle = (path) => {
    const pathMap = {
      "/": "Dashboard",
      "/properties": "Manage Properties",
      "/properties/add": "Add Property",
      "/users": "Manage Users",
      "/users/add": "Add User",
      "/sellers": "Manage Sellers",
      "/sellers/add": "Add Seller",
      "/agents": "Manage Agents",
      "/agents/add": "Add Agent",
      "/activity": "Activity Log",
      "/portfolio": "Manage Portfolio",
      "/portfolio/add": "Add Portfolio",
      "/email-templates": "Email Templates",
      "/email-templates/:id": "Email Template Details",
    };

    return pathMap[path] || "Dashboard";
  };

  const getBreadcrumbs = (path) => {
    if (path === "/") return ["Home"];
    if (path === "/dashboard") return ["Home", "Dashboard"];

    const segments = path.split("/").filter(Boolean);
    const breadcrumbs = ["Home"];

    segments.forEach((segment, index) => {
      if (segment === "add") {
        breadcrumbs.push("Add");
      } else if (segment !== "add") {
        const title = segment.charAt(0).toUpperCase() + segment.slice(1);
        breadcrumbs.push(title);
      }
    });

    return breadcrumbs;
  };

  return (
    <header className="header">
      <div className="header-left">
        <button className="menu-toggle" onClick={onMenuClick}>
          ☰
        </button>
        <div className="breadcrumbs">
          {getBreadcrumbs(currentPath).map((crumb, index) => (
            <span key={index}>
              {crumb}
              {index < getBreadcrumbs(currentPath).length - 1 && (
                <span className="separator"> / </span>
              )}
            </span>
          ))}
        </div>
      </div>

      <div className="header-right">
        <button className="theme-toggle"></button>
        <button 
          className="logout-btn"
          onClick={handleLogout}
          style={{
            backgroundColor: '#dc3545',
            color: 'white',
            border: 'none',
            padding: '8px 16px',
            borderRadius: '4px',
            cursor: 'pointer',
            fontSize: '14px',
            marginRight: '10px'
          }}
        >
          Logout
        </button>
        <div 
          className="user-profile"
          onClick={handleProfileClick}
          style={{ cursor: 'pointer' }}
        >
          <CgProfile size="1.4em" />
          <h6>Profile</h6>
        </div>
      </div>

      {/* Profile Modal */}
      {profileModalOpen && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}
        onClick={(e) => {
          if (e.target === e.currentTarget) {
            setProfileModalOpen(false);
          }
        }}
        >
          <div style={{
            backgroundColor: 'var(--cbre-bg-primary)',
            padding: '24px',
            borderRadius: '8px',
            border: '1px solid var(--cbre-border)',
            minWidth: '400px',
            maxWidth: '500px',
            maxHeight: '90vh',
            overflowY: 'auto'
          }}>
            <div style={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center',
              marginBottom: '20px'
            }}>
              <h4 style={{ 
                margin: 0,
                color: 'var(--cbre-text)'
              }}>
                Profile Details
              </h4>
              <button
                onClick={() => setProfileModalOpen(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  color: 'var(--cbre-text)',
                  fontSize: '24px',
                  cursor: 'pointer',
                  padding: 0,
                  width: '30px',
                  height: '30px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                ×
              </button>
            </div>

            {loadingProfile ? (
              <div style={{ 
                padding: '40px', 
                textAlign: 'center',
                color: 'var(--cbre-text-secondary)'
              }}>
                Loading profile...
              </div>
            ) : profileData ? (
              <>
                <div style={{ marginBottom: '20px' }}>
                  <label style={{ 
                    display: 'block',
                    marginBottom: '8px',
                    color: 'var(--cbre-text-secondary)',
                    fontSize: '14px',
                    fontWeight: 'bold'
                  }}>
                    First Name:
                  </label>
                  <div style={{
                    color: 'var(--cbre-text)',
                    fontSize: '14px',
                    paddingLeft: '4px'
                  }}>
                    {profileData.firstName || 'N/A'}
                  </div>
                </div>

                <div style={{ marginBottom: '20px' }}>
                  <label style={{ 
                    display: 'block',
                    marginBottom: '8px',
                    color: 'var(--cbre-text-secondary)',
                    fontSize: '14px',
                    fontWeight: 'bold'
                  }}>
                    Last Name:
                  </label>
                  <div style={{
                    color: 'var(--cbre-text)',
                    fontSize: '14px',
                    paddingLeft: '4px'
                  }}>
                    {profileData.lastName || 'N/A'}
                  </div>
                </div>

                <div style={{ marginBottom: '20px' }}>
                  <label style={{ 
                    display: 'block',
                    marginBottom: '8px',
                    color: 'var(--cbre-text-secondary)',
                    fontSize: '14px',
                    fontWeight: 'bold'
                  }}>
                    Email Address:
                  </label>
                  <div style={{
                    color: 'var(--cbre-text)',
                    fontSize: '14px',
                    paddingLeft: '4px'
                  }}>
                    {profileData.emailAddress || 'N/A'}
                  </div>
                </div>

                <div style={{ 
                  marginTop: '30px', 
                  paddingTop: '20px', 
                  borderTop: '1px solid var(--cbre-border)' 
                }}>
                  <h5 style={{ 
                    margin: '0 0 20px 0',
                    color: 'var(--cbre-text)',
                    fontSize: '16px'
                  }}>
                    Change Password
                  </h5>

                  <div style={{ marginBottom: '20px' }}>
                    <label style={{ 
                      display: 'block',
                      marginBottom: '8px',
                      color: 'var(--cbre-text)',
                      fontSize: '14px',
                      fontWeight: 'bold'
                    }}>
                      Current Password:
                    </label>
                    <input
                      type="password"
                      value={currentPassword}
                      onChange={(e) => setCurrentPassword(e.target.value)}
                      style={{
                        width: '100%',
                        padding: '10px',
                        border: '1px solid var(--cbre-border)',
                        borderRadius: '4px',
                        backgroundColor: 'var(--cbre-bg-secondary)',
                        color: 'var(--cbre-text)',
                        fontSize: '14px',
                        boxSizing: 'border-box'
                      }}
                      placeholder="Enter current password"
                    />
                  </div>

                  <div style={{ marginBottom: '20px' }}>
                    <label style={{ 
                      display: 'block',
                      marginBottom: '8px',
                      color: 'var(--cbre-text)',
                      fontSize: '14px',
                      fontWeight: 'bold'
                    }}>
                      New Password:
                    </label>
                    <div style={{ position: 'relative' }}>
                      <input
                        type={showNewPassword ? "text" : "password"}
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        style={{
                          width: '100%',
                          padding: '10px 40px 10px 10px',
                          border: '1px solid var(--cbre-border)',
                          borderRadius: '4px',
                          backgroundColor: 'var(--cbre-bg-secondary)',
                          color: 'var(--cbre-text)',
                          fontSize: '14px',
                          boxSizing: 'border-box'
                        }}
                        placeholder="Enter new password"
                      />
                      <button
                        type="button"
                        onClick={() => setShowNewPassword(!showNewPassword)}
                        style={{
                          position: 'absolute',
                          right: '10px',
                          top: '50%',
                          transform: 'translateY(-50%)',
                          background: 'none',
                          border: 'none',
                          cursor: 'pointer',
                          color: 'var(--cbre-text-secondary)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          padding: '4px'
                        }}
                        onMouseEnter={(e) => {
                          e.target.style.color = 'var(--cbre-text)';
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.color = 'var(--cbre-text-secondary)';
                        }}
                      >
                        {showNewPassword ? <FiEyeOff size={18} /> : <FiEye size={18} />}
                      </button>
                    </div>
                    {newPassword && (
                      <div style={{ marginTop: '8px', fontSize: '12px' }}>
                        <div style={{ color: 'var(--cbre-text-secondary)', marginBottom: '4px' }}>
                          Password must contain:
                        </div>
                        <div style={{ 
                          display: 'flex', 
                          flexDirection: 'column', 
                          gap: '2px',
                          paddingLeft: '8px'
                        }}>
                          <div style={{ 
                            color: validatePassword(newPassword).minLength ? '#28a745' : 'var(--cbre-text-secondary)' 
                          }}>
                            {validatePassword(newPassword).minLength ? '✓' : '○'} At least 8 characters
                          </div>
                          <div style={{ 
                            color: validatePassword(newPassword).hasUpperCase ? '#28a745' : 'var(--cbre-text-secondary)' 
                          }}>
                            {validatePassword(newPassword).hasUpperCase ? '✓' : '○'} One uppercase letter
                          </div>
                          <div style={{ 
                            color: validatePassword(newPassword).hasLowerCase ? '#28a745' : 'var(--cbre-text-secondary)' 
                          }}>
                            {validatePassword(newPassword).hasLowerCase ? '✓' : '○'} One lowercase letter
                          </div>
                          <div style={{ 
                            color: validatePassword(newPassword).hasNumber ? '#28a745' : 'var(--cbre-text-secondary)' 
                          }}>
                            {validatePassword(newPassword).hasNumber ? '✓' : '○'} One number
                          </div>
                          <div style={{ 
                            color: validatePassword(newPassword).hasSpecialChar ? '#28a745' : 'var(--cbre-text-secondary)' 
                          }}>
                            {validatePassword(newPassword).hasSpecialChar ? '✓' : '○'} One special character
                          </div>
                        </div>
                      </div>
                    )}
                  </div>

                  <div style={{ marginBottom: '20px' }}>
                    <label style={{ 
                      display: 'block',
                      marginBottom: '8px',
                      color: 'var(--cbre-text)',
                      fontSize: '14px',
                      fontWeight: 'bold'
                    }}>
                      Confirm New Password:
                    </label>
                    <div style={{ position: 'relative' }}>
                      <input
                        type={showConfirmPassword ? "text" : "password"}
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        style={{
                          width: '100%',
                          padding: '10px 40px 10px 10px',
                          border: newPassword && confirmPassword && newPassword !== confirmPassword 
                            ? '1px solid #dc3545' 
                            : '1px solid var(--cbre-border)',
                          borderRadius: '4px',
                          backgroundColor: 'var(--cbre-bg-secondary)',
                          color: 'var(--cbre-text)',
                          fontSize: '14px',
                          boxSizing: 'border-box'
                        }}
                        placeholder="Confirm new password"
                      />
                      <button
                        type="button"
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        style={{
                          position: 'absolute',
                          right: '10px',
                          top: '50%',
                          transform: 'translateY(-50%)',
                          background: 'none',
                          border: 'none',
                          cursor: 'pointer',
                          color: 'var(--cbre-text-secondary)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          padding: '4px'
                        }}
                        onMouseEnter={(e) => {
                          e.target.style.color = 'var(--cbre-text)';
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.color = 'var(--cbre-text-secondary)';
                        }}
                      >
                        {showConfirmPassword ? <FiEyeOff size={18} /> : <FiEye size={18} />}
                      </button>
                    </div>
                    {confirmPassword && newPassword && (
                      <div style={{ marginTop: '4px', fontSize: '12px' }}>
                        {newPassword === confirmPassword ? (
                          <div style={{ color: '#28a745' }}>✓ Passwords match</div>
                        ) : (
                          <div style={{ color: '#dc3545' }}>✗ Passwords do not match</div>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', marginTop: '20px' }}>
                  <button
                    onClick={() => {
                      setProfileModalOpen(false);
                      setCurrentPassword('');
                      setNewPassword('');
                      setConfirmPassword('');
                      setShowNewPassword(false);
                      setShowConfirmPassword(false);
                    }}
                    style={{
                      padding: '8px 16px',
                      backgroundColor: 'var(--cbre-bg-secondary)',
                      border: '1px solid var(--cbre-border)',
                      borderRadius: '4px',
                      color: 'var(--cbre-text)',
                      cursor: 'pointer'
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleChangePassword}
                    disabled={
                      savingProfile || 
                      !currentPassword.trim() || 
                      !newPassword.trim() || 
                      !confirmPassword.trim() ||
                      !validatePassword(newPassword).isValid ||
                      newPassword !== confirmPassword
                    }
                    style={{
                      padding: '8px 16px',
                      backgroundColor: (
                        savingProfile || 
                        !currentPassword.trim() || 
                        !newPassword.trim() || 
                        !confirmPassword.trim() ||
                        !validatePassword(newPassword).isValid ||
                        newPassword !== confirmPassword
                      ) ? 'var(--cbre-bg-secondary)' : '#0066cc',
                      border: '1px solid var(--cbre-border)',
                      borderRadius: '4px',
                      color: (
                        savingProfile || 
                        !currentPassword.trim() || 
                        !newPassword.trim() || 
                        !confirmPassword.trim() ||
                        !validatePassword(newPassword).isValid ||
                        newPassword !== confirmPassword
                      ) ? 'var(--cbre-text-secondary)' : 'white',
                      cursor: (
                        savingProfile || 
                        !currentPassword.trim() || 
                        !newPassword.trim() || 
                        !confirmPassword.trim() ||
                        !validatePassword(newPassword).isValid ||
                        newPassword !== confirmPassword
                      ) ? 'not-allowed' : 'pointer'
                    }}
                  >
                    {savingProfile ? 'Changing Password...' : 'Change Password'}
                  </button>
                </div>
              </>
            ) : (
              <div style={{ 
                padding: '40px', 
                textAlign: 'center',
                color: 'var(--cbre-text-secondary)'
              }}>
                Unable to load profile data
              </div>
            )}
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
